import { useState } from "react";
import { ScanLine } from "lucide-react";

import { InventoryAmlMpnPicker } from "@/components/inventory/InventoryAmlMpnPicker";
import { useHubToast } from "@/components/ui/HubToast";
import { Label } from "@/components/ui/Input";
import { ApiError } from "@/lib/api";
import {
  formatInventoryAmlMpnLabel,
  inventorySavedMpnNotOnAml,
  type InventoryAmlMpnAlternate,
} from "@/lib/inventoryAmlMpn";
import { verifyWarehouseRecountMpn } from "@/lib/warehouseRecountApi";
import { InboundReceiveMpnEntryModal } from "@/pages/shipping/InboundReceiveMpnEntryModal";

export function InventoryAmlMpnEntry({
  sku,
  alternates,
  selectedApprovedComponentId,
  savedApprovedComponentId,
  requireExplicitSelection = false,
  field = false,
  disabled = false,
  onSelect,
}: {
  sku: string;
  alternates: InventoryAmlMpnAlternate[];
  selectedApprovedComponentId: string | null;
  savedApprovedComponentId?: string | null;
  /** Do not treat the only approved part as already chosen. */
  requireExplicitSelection?: boolean;
  /** Single manufacturer part number uses the same height as a text input. */
  field?: boolean;
  disabled?: boolean;
  onSelect: (approvedComponentId: string) => void;
}): JSX.Element | null {
  const { showToast } = useHubToast();
  const [open, setOpen] = useState(false);
  const [verifying, setVerifying] = useState(false);

  if (alternates.length === 0) return null;

  if (!requireExplicitSelection && alternates.length <= 1) {
    return (
      <InventoryAmlMpnPicker
        alternates={alternates}
        selectedApprovedComponentId={selectedApprovedComponentId}
        savedApprovedComponentId={savedApprovedComponentId}
        field={field}
        disabled={disabled}
        onSelect={onSelect}
      />
    );
  }

  const selectedAlternate =
    alternates.find((alternate) => alternate.id === selectedApprovedComponentId) ?? null;
  const savedNotOnAml = inventorySavedMpnNotOnAml({
    alternates,
    savedApprovedComponentId: savedApprovedComponentId ?? null,
  });

  const verify = async (mpn: string, manufacturerName: string | null): Promise<void> => {
    setVerifying(true);
    try {
      const result = await verifyWarehouseRecountMpn(sku, mpn, manufacturerName);
      if (result.status === "choose_manufacturer") {
        showToast({
          message: "Choose the manufacturer printed on the label.",
          tone: "error",
        });
        return;
      }
      if (result.status !== "matched") {
        showToast({
          message:
            manufacturerName != null && manufacturerName.trim() !== ""
              ? "That manufacturer is not approved for this part number."
              : `MPN ${mpn} does not match the approved vendor list for ${sku}.`,
          tone: "error",
        });
        return;
      }
      onSelect(String(result.approved_component_id));
      setOpen(false);
      showToast({ message: "Manufacturer part number verified.", tone: "success" });
    } catch (error) {
      showToast({
        message: error instanceof ApiError ? error.message : String(error),
        tone: "error",
      });
    } finally {
      setVerifying(false);
    }
  };

  return (
    <>
      <div>
        {savedNotOnAml ? (
          <p className="mb-2 text-sm text-amber-950">
            This container&apos;s saved MPN is not on the current approved vendor list.
          </p>
        ) : null}
        <Label>Manufacturer part number</Label>
        <button
          type="button"
          disabled={disabled || verifying}
          className="mt-1 flex h-10 w-full items-center rounded-md border border-slate-300 bg-white px-3 text-left text-sm text-slate-900 focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 disabled:cursor-not-allowed disabled:opacity-60"
          onClick={() => setOpen(true)}
        >
          {selectedAlternate != null ? (
            <span className="truncate font-mono">{formatInventoryAmlMpnLabel(selectedAlternate)}</span>
          ) : (
            <span className="inline-flex items-center gap-2 text-slate-500">
              <ScanLine className="h-4 w-4 shrink-0" aria-hidden />
              Scan or enter MPN
            </span>
          )}
        </button>
      </div>
      <InboundReceiveMpnEntryModal
        open={open}
        sku={sku}
        verifying={verifying}
        onVerify={(mpn, manufacturerName) => {
          void verify(mpn, manufacturerName);
        }}
        onClose={() => setOpen(false)}
      />
    </>
  );
}
